<script setup lang="ts">
/**
 * "แท็กลูกค้า" from the chat header's "…" menu (owner 17 ต.ค. 69: tag editing left the customer panel). Same rules as
 * ContactTags (any staff may tag, saved at once, the team sees it); "จัดการแท็ก" for the people who may manage them.
 */
import { computed, inject } from 'vue'
import { routerKey } from 'vue-router'
import type { ConversationDetail } from '../../api/types'
import { autoTagTitle } from '../../lib/autoTags'
import { useClock } from '../../stores/clock'
import { useCollab } from '../../stores/collab'
import LibDialog from '../library/LibDialog.vue'
import Icon from '../ui/Icon.vue'

const props = defineProps<{ detail: ConversationDetail }>()
const emit = defineEmits<{ close: [] }>()
const collab = useCollab()
const clock = useClock()
const router = inject(routerKey, null)

const tags = computed(() => props.detail.tags ?? [])
const ids = computed(() => new Set(tags.value.map((t) => t.id)))
if (!collab.tagDefs.length) void collab.loadTags()

function toggle(id: string): void {
  const next = ids.value.has(id) ? tags.value.map((t) => t.id).filter((x) => x !== id) : [...ids.value, id]
  void collab.setTags(props.detail.id, next)
}

function manage(): void {
  emit('close')
  void router?.push({ name: 'config', params: { section: 'tags' } })
}
</script>

<template>
  <LibDialog title="แท็กลูกค้า" :sub="`${detail.contact.name} · ทั้งทีมเห็น`" :width="400" @close="emit('close')">
    <div class="tp" role="listbox" aria-multiselectable="true" aria-label="เลือกแท็กลูกค้า">
      <button
        v-for="d in collab.tagDefs"
        :key="d.id"
        type="button"
        role="option"
        class="tp-opt"
        :aria-selected="ids.has(d.id)"
        @click="toggle(d.id)"
      >
        <span class="tp-cb" :class="{ on: ids.has(d.id) }"><Icon v-if="ids.has(d.id)" name="tick" /></span>
        <span class="tone-dot" :class="'td-' + d.tone"></span>
        <span class="grow">{{ d.name }}</span>
        <span class="tp-n mono">{{ d.count }}</span>
      </button>
      <p v-if="!collab.tagDefs.length" class="tp-empty">ยังไม่มีแท็ก ให้ผู้จัดการเพิ่มในตั้งค่า</p>
    </div>
    <p v-if="tags.some((t) => t.auto)" class="tp-auto">
      <Icon name="info" /><span>แท็กที่ระบบติดให้: {{ tags.filter((t) => t.auto).map((t) => autoTagTitle(t, clock.now) ?? t.name).join(' · ') }}</span>
    </p>
    <template #footer>
      <button v-if="collab.canManageTags" type="button" class="btn tp-m" @click="manage"><Icon name="settings" />จัดการแท็ก</button>
      <span class="grow"></span>
      <button type="button" class="btn" autofocus @click="emit('close')">เสร็จ</button>
    </template>
  </LibDialog>
</template>

<style scoped>
.tp {
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.tp-opt {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  height: 40px;
  padding: 0 10px;
  border-radius: 8px;
  font-size: 14px;
  line-height: 22px;
  white-space: nowrap;
}
.tp-opt:hover {
  background: #f4f6f5;
}
.tp-cb {
  width: 18px;
  height: 18px;
  border-radius: 5px;
  border: 1.5px solid #9aaba3;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: none;
  color: #fff;
}
.tp-cb.on {
  background: var(--ac);
  border-color: var(--ac);
}
.tp-cb :deep(.ic) {
  width: 12px;
  height: 12px;
  stroke-width: 2.8;
}
.tp-n {
  font-size: 12px;
  color: var(--mut);
}
.tp-empty {
  padding: 8px 10px;
  font-size: 13px;
  line-height: 20px;
  color: var(--mut);
}
.tp-auto {
  display: flex;
  align-items: flex-start;
  gap: 6px;
  margin-top: 10px;
  font-size: 12px;
  line-height: 18px;
  color: var(--mut);
}
.tp-auto :deep(.ic) {
  flex: none;
  width: 14px;
  height: 14px;
  margin-top: 2px;
}
.tp-m :deep(.ic) {
  color: var(--ac);
}
</style>
